import React, { useEffect, useState } from "react";
import {
    Row,
    Col,
    FormGroup,
    Label,
    Input,
    Container,
    FormFeedback,
    Form,
    Button,
    ButtonGroup,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import ResultHandler from "../../../components/ResultHandler";
import FormButtonHandler from "../../../components/FormButtonHandler";
import { useResponse } from "../../../hooks/useResponse";
import { useTranslation } from "react-i18next";
import {
    getStatusLeadContact,
    editStatusLeadContact,
    getStatusLeadContacts,
} from "../../../api/statusLeadContact";

const LEVEL_OPTIONS = [
    { value: 'start', label: 'Start', color: 'primary' },
    { value: 'failed', label: 'Failed', color: 'danger' },
    { value: 'final', label: 'Final', color: 'success' },
    { value: null, label: 'Default', color: 'secondary' },
];

const EditStatusLeadContact = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [statusDealData, setStatusDealData] = useState(null);
    const [usedLevels, setUsedLevels] = useState([]);
    const [levelStatuses, setLevelStatuses] = useState({});
    const [statusesById, setStatusesById] = useState({});
    const [modal, setModal] = useState(false);
    const [selectedLevel, setSelectedLevel] = useState(null);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(t("Name is required")),
        level: Yup.string()
            .nullable()
            .oneOf(['start', 'failed', 'final', null], t("Invalid level selected")),
    });

    const { setData, loading, error, success } = useResponse(editStatusLeadContact, () => {
        navigate("/statusLeadContacts/");
    });

    useEffect(() => {
        const fetchStatusDeal = async () => {
            try {
                const res = await getStatusLeadContact(id);
                const data = res?.data;
                setStatusDealData({
                    name: data.name || "",
                    level: data.level || null,
                });

                const allStatusesRes = await getStatusLeadContacts({}, { page: 1, limit: 100 });
                if (allStatusesRes?.data) {
                    const existingLevels = allStatusesRes.data.map(status => status.level);
                    const statusByLevel = {};
                    const statusesMap = {};

                    allStatusesRes.data.forEach(status => {
                        if (status.level) {
                            statusByLevel[status.level] = {
                                name: status.name,
                                id: status._id
                            };
                        }
                        statusesMap[status._id] = status;
                    });

                    setUsedLevels(existingLevels);
                    setLevelStatuses(statusByLevel);
                    setStatusesById(statusesMap);
                }
            } catch (err) {
                console.error("Failed to fetch status lead contact", err);
            }
        };

        fetchStatusDeal();
    }, [id]);

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: statusDealData || {
            name: "",
            level: null,
        },
        validationSchema,
        onSubmit: (values) => {
            if (
                usedLevels.includes(values.level) &&
                values.level !== statusDealData?.level &&
                !modal
            ) {
                setSelectedLevel(values.level);
                setModal(true);
            } else {
                const updatedData = {
                    name: values.name,
                    level: values.level,
                };
                setData({ id, data: updatedData, type: "edit" });
            }
        },
    });

    const handleLevelSelect = (level) => {
        validation.setFieldValue('level', level);
    };

    const toggle = () => {
        setModal(!modal);
        setSelectedLevel(null);
    };

    const handleOverride = async () => {
        try {
            const currentStatus = levelStatuses[selectedLevel];
            if (currentStatus && currentStatus.id) {
                await editStatusLeadContact(currentStatus.id, {
                    name: statusesById[currentStatus.id].name,
                    level: null,
                });
            }

            setData({
                id,
                data: {
                    name: validation.values.name,
                    level: validation.values.level,
                },
                type: "edit",
            });

            setModal(false);

            const updatedLevelStatuses = { ...levelStatuses };
            delete updatedLevelStatuses[selectedLevel];
            setLevelStatuses(updatedLevelStatuses);
        } catch (err) {
            console.error("Failed to override level:", err);
            alert(t("Failed to override level. Please try again."));
        }
    };

    return (
        <div className="page-content">
            <Container fluid={true}>
                <Breadcrumbs title={`${t("btns.Edit")} ${t("Status Lead Contact")}`} />

                <Form
                    className="needs-validation"
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                    }}
                >
                    <Row>
                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Name")}</Label>
                                <Input
                                    name="name"
                                    placeholder={t("Name")}
                                    type="text"
                                    onChange={validation.handleChange}
                                    value={validation.values.name}
                                    invalid={!!(validation.touched.name && validation.errors.name)}
                                />
                                <FormFeedback>{validation.errors.name}</FormFeedback>
                            </FormGroup>
                        </Col>

                        <Col md="6">
                            <FormGroup className="mb-3">
                                <Label>{t("Level")}</Label>
                                <div>
                                    <ButtonGroup className="w-100 gap-2">
                                        {LEVEL_OPTIONS.map((option) => {
                                            const isSelected = validation.values.level === option.value;
                                            const currentStatus = levelStatuses[option.value];
                                            const isCurrentLevel = statusDealData && statusDealData.level === option.value;
                                            return (
                                                <Button
                                                    key={option.value}
                                                    color={isSelected ? option.color : 'light'}
                                                    outline={!isSelected}
                                                    onClick={() => handleLevelSelect(option.value)}
                                                    className={`flex-grow-1 text-capitalize d-flex align-items-center justify-content-center gap-2 ${isSelected ? 'shadow-sm' : ''}`}
                                                    style={{
                                                        minWidth: '120px',
                                                        border: isSelected ? `2px solid var(--bs-${option.color})` : '1px solid #dee2e6',
                                                        transition: 'all 0.2s ease',
                                                        height: '50px'
                                                    }}
                                                >
                                                    <span>{t(option.label)}</span>
                                                    {currentStatus && !isCurrentLevel && (
                                                        <span className="position-absolute" style={{ right: '10px', fontSize: '12px' }} />
                                                    )}
                                                </Button>
                                            );
                                        })}
                                    </ButtonGroup>
                                    {validation.touched.level && validation.errors.level && (
                                        <div className="text-danger mt-1">
                                            {validation.errors.level}
                                        </div>
                                    )}
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>

                    <FormButtonHandler btnTxt={t("Update")} loading={loading} />

                    <ResultHandler
                        error={error}
                        success={success}
                        successMsg={t("Updated Successfully!")}
                    />
                </Form>

                <Modal isOpen={modal} toggle={toggle}>
                    <ModalHeader toggle={toggle}>{t("Level Already in Use")}</ModalHeader>
                    <ModalBody>
                        {selectedLevel && levelStatuses[selectedLevel] && (
                            <p>
                                {t("This level is already taken by ")} <strong>{levelStatuses[selectedLevel].name}</strong>.
                                {t("Do you want to override it? The level will be removed from the existing status.")}
                            </p>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggle}>
                            {t("Cancel")}
                        </Button>
                        <Button color="primary" onClick={handleOverride}>
                            {t("Override")}
                        </Button>
                    </ModalFooter>
                </Modal>
            </Container>
        </div>
    );
};

export default EditStatusLeadContact;
